import React from "react";
import moment from "moment";
import {
  IoAlertCircleOutline,
  IoTimeOutline,
  IoCheckmarkCircleOutline,
  IoCheckmarkDoneOutline,
} from "react-icons/io5";
import {
  getStatusStyles,
  getPriorityStyles,
  getSourceTypeLabel,
  formatEnumForDisplay,
  getMarkupFromMetadata,
  DEFAULT_NO_IMAGE,
} from "./constants";

const IssueStatusIcon = ({ status }) => {
  const s = (status || "").toUpperCase().replace(/\s+/g, "_");
  if (s === "OPEN")
    return <IoAlertCircleOutline color="#dc2626" size={14} aria-hidden />;
  if (s === "IN_PROGRESS")
    return <IoTimeOutline color="#ea580c" size={14} aria-hidden />;
  if (s === "RESOLVED")
    return <IoCheckmarkCircleOutline color="#16a34a" size={14} aria-hidden />;
  if (s === "DONE")
    return <IoCheckmarkDoneOutline color="#fff" size={14} aria-hidden />;
  return null;
};

const pillBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 500,
  textTransform: "capitalize",
  width: "fit-content",
  flexShrink: 0,
};

export default function ProjectCardIssues({ list, onIssueClick }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {list.map((issue) => (
        <div
          key={issue.id}
          role="button"
          tabIndex={0}
          onClick={() => onIssueClick?.(issue.id)}
          onKeyDown={(e) => e.key === "Enter" && onIssueClick?.(issue.id)}
          style={{
            backgroundColor: "#fff",
            padding: 12,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 12,
            borderBottom: "1px solid #D7D8D9",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
                flex: 1,
              }}
            >
              <div
                style={{
                  position: "relative",
                  width: 64,
                  height: 64,
                  borderRadius: 10,
                  overflow: "hidden",
                  backgroundColor: "#e5e7eb",
                  flexShrink: 0,
                }}
              >
                <img
                  src={issue.image_url || DEFAULT_NO_IMAGE}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                {(() => {
                  const markup = getMarkupFromMetadata(issue.metadata);
                  return markup != null ? (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 2,
                        right: 2,
                        minWidth: 18,
                        height: 18,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 4px",
                        borderRadius: 9999,
                        backgroundColor: "#2563eb",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 500,
                      }}
                    >
                      {markup}
                    </span>
                  ) : null;
                })()}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p
                  style={{
                    fontWeight: 500,
                    color: "#0d0d0e",
                    fontSize: 14,
                    margin: 0,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {issue.description || "-"}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "#949DAA",
                    margin: 0,
                    marginTop: 2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {issue.issue_category
                    ? `${issue.issue_category.activityType || ""} • ${issue.issue_category.category || ""}`
                    : "-"}
                </p>
              </div>
            </div>
            <span style={{ ...pillBase, ...getStatusStyles(issue.status) }}>
              <IssueStatusIcon status={issue.status} />
              {formatEnumForDisplay(issue.status) || "-"}
            </span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#949DAA" }}>Source type</label>
            <span
              style={{
                ...pillBase,
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #e5e7eb",
              }}
            >
              {getSourceTypeLabel(issue.source_type)}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, color: "#949DAA" }}>Issue Created</label>
              <span style={{ fontSize: 14, color: "#0d0d0e" }}>
                {issue.created_at ? moment(issue.created_at).format("MMM DD, YYYY") : "-"}
              </span>
            </div>
            <span
              style={{
                ...pillBase,
                ...getPriorityStyles(issue.issue_category?.severity),
              }}
            >
              {formatEnumForDisplay(issue.issue_category?.severity) ?? "-"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
